import { z } from 'zod';

/**
 * Zones de stockage disponibles
 */
export const stockageZones = [
  'positive',
  'negative',
  'frigo',
  'reserve',
  'cave',
  'entretien',
] as const;

export type ZoneStockage = typeof stockageZones[number];

/**
 * Schema pour la mise à jour de quantité de stock
 */
export const stockUpdateSchema = z.object({
  quantite: z
    .number()
    .min(0, 'La quantité ne peut pas être négative'),
});

export type StockUpdateInput = z.infer<typeof stockUpdateSchema>;

/**
 * Schema pour le transfert de stock entre zones
 */
export const stockTransferSchema = z.object({
  produit_id: z.string().uuid('ID de produit invalide'),
  zone_source: z.enum(stockageZones, {
    errorMap: () => ({ message: 'Zone source invalide' }),
  }),
  zone_destination: z.enum(stockageZones, {
    errorMap: () => ({ message: 'Zone de destination invalide' }),
  }),
  quantite: z
    .number()
    .positive('La quantité doit être supérieure à 0'),
}).refine(
  (data) => data.zone_source !== data.zone_destination,
  {
    message: 'La zone source et la zone de destination doivent être différentes',
    path: ['zone_destination'],
  }
);

export type StockTransferInput = z.infer<typeof stockTransferSchema>;

/**
 * Schema pour l'ajustement de stock (inventaire)
 */
export const stockAdjustmentSchema = z.object({
  produit_id: z.string().uuid('ID de produit invalide'),
  zone: z.enum(stockageZones, {
    errorMap: () => ({ message: 'Zone invalide' }),
  }),
  nouvelle_quantite: z
    .number()
    .min(0, 'La quantité ne peut pas être négative'),
  raison: z.string().optional(),
});

export type StockAdjustmentInput = z.infer<typeof stockAdjustmentSchema>;

/**
 * Types de mouvements de stock
 */
export const mouvementTypes = [
  'entree',
  'sortie',
  'transfert',
  'ajustement',
  'perte',
  'inventaire',
] as const;

export type MouvementType = typeof mouvementTypes[number];

/**
 * Schema pour l'enregistrement d'un mouvement de stock
 */
export const mouvementSchema = z.object({
  produit_id: z.string().uuid('ID de produit invalide'),
  zone: z.enum(stockageZones),
  type: z.enum(mouvementTypes),
  quantite: z.number(),
  zone_destination: z.enum(stockageZones).optional(),
  raison: z.string().max(500, 'La raison ne peut pas dépasser 500 caractères').optional(),
});

export type MouvementInput = z.infer<typeof mouvementSchema>;

/**
 * Schema pour la saisie vocale (résultat parsé)
 */
export const voiceInputItemSchema = z.object({
  produit_nom: z.string().min(1, 'Nom du produit requis'),
  quantite: z.number().positive('La quantité doit être positive'),
  unite: z.string().optional(),
  zone: z.enum(stockageZones).optional(),
});

export type VoiceInputItem = z.infer<typeof voiceInputItemSchema>;

/**
 * Schema pour une session d'inventaire
 */
export const inventorySessionSchema = z.object({
  restaurant_id: z.string().uuid('ID de restaurant invalide'),
  zone: z.enum(stockageZones).optional(),
  date_debut: z.date(),
  date_fin: z.date().optional(),
  statut: z.enum(['en_cours', 'termine', 'annule']),
  items: z.array(z.object({
    produit_id: z.string().uuid(),
    quantite_comptee: z.number().min(0),
    quantite_theorique: z.number().min(0),
    ecart: z.number(),
  })),
});

export type InventorySessionInput = z.infer<typeof inventorySessionSchema>;
